import { useCallback, useEffect, useMemo, useState } from "react";

type MaybeUpdater<T> = Partial<T> | ((prev: T) => T);

const memoryStore = new Map<string, string>();

function readItem(key: string): string | null {
  if (typeof window === "undefined") {
    return memoryStore.get(key) ?? null;
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return memoryStore.get(key) ?? null;
  }
}

function writeItem(key: string, value: string) {
  if (typeof window === "undefined") {
    memoryStore.set(key, value);
    return;
  }
  try {
    sessionStorage.setItem(key, value);
  } catch {
    memoryStore.set(key, value);
  }
}

/**
 * Persist lightweight map preferences for the current session.
 * Session storage keeps state per device without server calls.
 */
export function useMapPreferences<T extends object>(
  key: string,
  defaults: T,
): { prefs: T; setPrefs: (update: MaybeUpdater<T>) => void; loaded: boolean } {
  const storageKey = useMemo(() => key, [key]);
  const [prefs, setPrefs] = useState<T>(defaults);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const raw = readItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Partial<T>;
        setPrefs({ ...defaults, ...parsed });
      } catch {
        setPrefs(defaults);
      }
    } else {
      setPrefs(defaults);
    }
    setLoaded(true);
  }, [defaults, storageKey]);

  useEffect(() => {
    if (!loaded) return;
    writeItem(storageKey, JSON.stringify(prefs));
  }, [loaded, prefs, storageKey]);

  const update = useCallback(
    (update: MaybeUpdater<T>) => {
      setPrefs((prev) => {
        if (typeof update === "function") {
          return (update as (prev: T) => T)(prev);
        }
        return { ...(prev as T), ...update };
      });
    },
    [],
  );

  return { prefs, setPrefs: update, loaded };
}
